import Box from '@mui/material/Box';
import { Formik, FormikErrors } from 'formik';
import { memo, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';

import {
    KeyGetGetResourcesByType,
    PutSetResourcesResponse,
    Resource,
    ResourceType,
    useMutationPutSetResources,
    useQueryGetGetResourcesByType,
} from '../../backend';
import { errorToastConfig } from '../../utils/utils';
import { FormPageFooter } from '../navigation/FormPageFooter/FormPageFooter';
import { ResourceFieldsContainer } from '../Resources/ResourceContent/ResourceFieldsContainer';
import { ResourceHeader } from '../Resources/ResourceHeader/ResourceHeader';
import { validateResource } from './ResourcesPage.utils';
import { StyledForm } from './StyledForm.style';

export interface ResourcesPageFormValues {
    //in ResourceFieldsLineContainer display null, if nullable
    resources: Resource[] | null;
}

export const resourcesPollingInterval = 30 * 1000;

export const ResourcesPage = memo(function ResourcesPage() {
    const queryClient = useQueryClient();
    const [resourceType, setResourceType] = useState<ResourceType>(ResourceType.PARKING);
    const [enableRefetch, setEnableRefetch] = useState<boolean>(true);

    const { data: resources } = useQueryGetGetResourcesByType(
        {
            type: resourceType,
            includeNonActive: true,
        },
        {
            refetchInterval: resourcesPollingInterval,
            enabled: enableRefetch,
            onError: () => {
                toast(<FormattedMessage id={'resourcesForm.error.default'} />, errorToastConfig);
            },
            select: response => {
                return {
                    ...response,
                    data: response.data.sort((a, b) => {
                        return a.name.localeCompare(b.name, undefined, { numeric: true });
                    }),
                };
            },
            notifyOnChangeProps: ['data', 'error'],
        },
    );

    const { mutate, isLoading } = useMutationPutSetResources({
        onError: e => {
            toast(e?.message, errorToastConfig);
        },
        onSuccess: (response: PutSetResourcesResponse) => {
            queryClient.setQueriesData(
                { queryKey: [KeyGetGetResourcesByType, { includeNonActive: true, type: resourceType }] },
                response,
            );
        },
    });

    const [search, setSearch] = useState<string>('');

    const initialValues = useMemo(
        (): ResourcesPageFormValues => ({
            resources,
        }),
        [resources],
    );

    const handleSubmit = useCallback(
        (values: ResourcesPageFormValues) => {
            mutate({
                resource: values.resources,
            });
            setEnableRefetch(true);
        },
        [mutate],
    );

    const handleValidate = useCallback((values: ResourcesPageFormValues) => {
        const errors: FormikErrors<ResourcesPageFormValues> = {};

        values.resources?.forEach((r, index) => {
            validateResource(index, r, errors, values);
        });

        return errors;
    }, []);

    return (
        <Box display="flex" flexDirection="column" alignItems="center" width="100%" height="100%">
            <ResourceHeader search={search} setSearch={setSearch} />
            <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize validate={handleValidate}>
                {props => (
                    <StyledForm>
                        <ResourceFieldsContainer
                            search={search}
                            resourceType={resourceType}
                            setResourceType={setResourceType}
                        />
                        <FormPageFooter isLoading={isLoading} setEnableRefetch={setEnableRefetch} {...props} />
                    </StyledForm>
                )}
            </Formik>
        </Box>
    );
});
